import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const { pathname } = useLocation();
  const base = pathname === "/" ? "" : "/";
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const links = [
    { label: t("nav.mission"), href: `${base}#manifesto` },
    { label: t("nav.causes"), href: `${base}#causes` },
    { label: t("nav.campaigns"), href: `${base}#campaigns` },
    { label: t("nav.wall"), href: `${base}#donor-wall` },
    { label: t("nav.about"), href: "/about" },
    { label: t("nav.contact"), href: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-background/80 backdrop-blur-md border-b border-border" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href={`${base}#top`} data-testid="logo-link" className="flex flex-col leading-none">
          <span className="font-serif text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
            Findivine
          </span>
          <span className="text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-primary font-bold">
            Divinity in Humanity
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors relative group whitespace-nowrap"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-xs font-semibold text-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <Languages size={14} /> {lang === "en" ? "हिंदी" : "English"}
          </button>
          <a
            href={`${base}#donate`}
            data-testid="header-donate-btn"
            className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:scale-105 transition-transform whitespace-nowrap"
          >
            {t("nav.donateNow")}
          </a>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <button onClick={toggle} data-testid="lang-toggle-mobile"
                  className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 text-xs font-semibold text-foreground">
            <Languages size={13} /> {lang === "en" ? "हिं" : "EN"}
          </button>
          <button data-testid="menu-toggle" className="text-foreground" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-background border-b border-border px-6 py-6 flex flex-col gap-5 shadow-lg">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)}
               data-testid={`mnav-${l.href.replace("#", "")}`}
               className="text-base font-medium text-foreground/80 py-1.5">
              {l.label}
            </a>
          ))}
          <a href={`${base}#donate`} onClick={() => setOpen(false)} data-testid="mnav-donate-btn"
             className="rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold text-center">
            {t("nav.donateNow")}
          </a>
        </div>
      )}
    </motion.header>
  );
};
